import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { NewsArticle } from '../../../Model/News/newsArticle.js'
import { newsImageUrl } from '../../../Model/News/newsArticles.js'
import { feedbackAvailable, openFeedbackForm } from '../../../Services/Telemetry/Feedback.js'
import { SpinIdCardIcon } from '../../Component/SpinIdCard/SpinIdCardIcon.js'
import type { DirectiveProps } from './remarkNewsDirectives.js'

export const NewsArticleContext = createContext<Pick<NewsArticle, 'dir' | 'hero'>>({ dir: '' })

type DirectiveComponentProps = { attributes: Record<string, string>; children?: ReactNode }

const warnDev = (message: string) => {
  if (import.meta.env.DEV) {
    console.warn(message)
  }
}

/** Icons an article may show in its hero via `:::hero{icon="…"}`. */
export const NEWS_ICONS: Record<string, ReactNode> = {
  spin: <SpinIdCardIcon variant="display" className="h-16 w-24 shrink-0 text-primary-700" />
}

const Hero = ({ attributes, children }: DirectiveComponentProps) => {
  const article = useContext(NewsArticleContext)
  const heroUrl = newsImageUrl(article, article.hero)
  const icon = attributes.icon ? NEWS_ICONS[attributes.icon] : undefined
  if (attributes.icon && !icon) {
    warnDev(`News :::hero with unknown icon "${attributes.icon}"`)
  }
  return (
    <section className="mb-10 overflow-hidden rounded-2xl border border-emerald-100 bg-emerald-50/70">
      {heroUrl && <img src={heroUrl} alt="" className="not-prose max-h-80 w-full object-cover" />}
      <div className="flex gap-6 p-6 md:p-8">
        {icon && <div className="not-prose hidden pt-2 sm:block">{icon}</div>}
        <div className="min-w-0 flex-1 [&>:first-child]:mt-0 [&>:last-child]:mb-0">{children}</div>
      </div>
    </section>
  )
}

const Button = ({ attributes, children }: DirectiveComponentProps) => {
  const className = attributes.variant === 'secondary' ? 'btn btn-outline' : 'btn btn-primary'
  if (!attributes.to) {
    warnDev('News ::button without a `to` attribute')
    return <span>{children}</span>
  }
  if (/^https?:\/\//.test(attributes.to)) {
    return (
      <a href={attributes.to} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    )
  }
  return (
    <Link to={attributes.to} className={className}>
      {children}
    </Link>
  )
}

const MissingImage = ({ src }: { src: string }) =>
  import.meta.env.DEV ? (
    <div className="flex h-48 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-sm text-slate-400">
      Missing image: {src}
    </div>
  ) : null

const Figure = ({ attributes, children }: DirectiveComponentProps) => {
  const article = useContext(NewsArticleContext)
  const url = newsImageUrl(article, attributes.src)
  if (!url && !import.meta.env.DEV) {
    return null
  }
  return (
    <figure>
      {url ? (
        <img src={url} alt={attributes.alt ?? ''} className="rounded-lg border border-slate-200" />
      ) : (
        <MissingImage src={attributes.src} />
      )}
      {children && <figcaption>{children}</figcaption>}
    </figure>
  )
}

const Details = ({ attributes, children }: DirectiveComponentProps) => (
  <details
    id={attributes.id}
    open={!!attributes.id && window.location.hash === `#${attributes.id}`}
    className="my-3 scroll-mt-24 rounded-lg border border-slate-200 bg-white px-4 py-3"
  >
    <summary className="cursor-pointer font-medium text-slate-900">{attributes.summary}</summary>
    <div className="[&>:first-child]:mt-3 [&>:last-child]:mb-0">{children}</div>
  </details>
)

export const NEWS_ACTIONS: Record<string, () => void> = {
  feedback: () => void openFeedbackForm()
}

const ActionLink = ({ attributes, children }: DirectiveComponentProps) => {
  const action = NEWS_ACTIONS[attributes.action]
  if (!action) {
    warnDev(`News :link with unknown action "${attributes.action}"`)
    return <span>{children}</span>
  }
  return (
    <>
      <button type="button" onClick={action} className="link link-primary font-medium">
        {children}
      </button>
      {attributes.action === 'feedback' && import.meta.env.DEV && !feedbackAvailable() && (
        <span className="ml-2 text-xs text-amber-600">(dev: no Sentry DSN, so the feedback form will not open)</span>
      )}
    </>
  )
}

/** Name → component for every directive an article may use. Add a component here to make it available. */
export const NEWS_DIRECTIVES: Record<string, (props: DirectiveComponentProps) => ReactNode> = {
  hero: Hero,
  button: Button,
  figure: Figure,
  details: Details,
  link: ActionLink
}

/** Unknown directives keep their content; a text directive also gets its `:name` back, since `a:b` in prose parses as one. */
export const NewsDirective = ({ name, kind, attributes, children }: DirectiveProps & { children?: ReactNode }) => {
  const Component = NEWS_DIRECTIVES[name]
  if (Component) {
    return <Component attributes={attributes}>{children}</Component>
  }
  if (kind === 'textDirective') {
    return (
      <>
        :{name}
        {children}
      </>
    )
  }
  warnDev(`Unknown news directive "${name}"`)
  return <div>{children}</div>
}
